import { useCallback, useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'user-theme';
export const THEME_CHANGE_EVENT = 'syllera-theme-change';

const ACCOUNT_THEME_PATHS = [
  '/user-dashboard',
  '/teacher-dashboard',
  '/parent-dashboard',
  '/admin-dashboard',
  '/school-dashboard',
  '/business/dashboard',
];

export function isAccountThemePath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  return ACCOUNT_THEME_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function getStoredTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function setDocumentTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme === 'dark' ? 'dark' : 'light');
}

export function syncThemeWithRoute(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  const visual = isAccountThemePath(pathname) ? getStoredTheme() : 'light';
  setDocumentTheme(visual);
  return visual;
}

export function applyTheme(theme) {
  const next = theme === 'dark' ? 'dark' : 'light';
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Ignore quota / private-mode failures
  }
  const visual = typeof window !== 'undefined' && !isAccountThemePath() ? 'light' : next;
  setDocumentTheme(visual);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: next }));
  }
  return next;
}

export function toggleStoredTheme() {
  return applyTheme(getStoredTheme() === 'dark' ? 'light' : 'dark');
}

export function useTheme() {
  const [theme, setThemeState] = useState(getStoredTheme);

  useEffect(() => {
    applyTheme(getStoredTheme());
    const handleChange = (event) => {
      if (event?.detail === 'dark' || event?.detail === 'light') {
        setThemeState(event.detail);
      }
    };
    const handleStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY) {
        setThemeState(event.newValue === 'dark' ? 'dark' : 'light');
      }
    };
    window.addEventListener(THEME_CHANGE_EVENT, handleChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(applyTheme(next));
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(toggleStoredTheme());
  }, []);

  return { theme, setTheme, toggleTheme, isDark: theme === 'dark' };
}
